import { Radio, CircleDashed } from 'lucide-react'
import { useTradingStore } from '../hooks/useTradingStore'

/**
 * What this container is trading, right now.
 *
 * Deliberately distinct from StrategySelector: that control shows the strategy
 * you have SELECTED, which is not the same thing once a run has started or been
 * stopped. This reads the active run — instrument, strategy and preset — so a
 * glance at any page answers "what is this box actually doing", which matters
 * when algo1 and algo2 are separate deployments on identical dashboards.
 *
 * The instrument already travels the whole chain (registry `_active_strategy`
 * -> to_dict -> ActiveStrategyResponse -> store); it was simply never rendered.
 */
export default function ActiveInstrumentBadge() {
  const { activeStrategy, strategyStatus, runMode } = useTradingStore()

  const isRunning = strategyStatus === 'running' && !!activeStrategy
  const isLive = isRunning && runMode === 'live'
  const instrument = activeStrategy?.instrument

  // Mode wording follows the header's existing convention: everything that is
  // not a live run reads as "Backtest" there, so don't invent new labels here.
  const modeLabel = !isRunning ? 'Idle' : isLive ? 'Live' : 'Backtest'

  const accent = isLive
    ? { text: 'text-[#00ff88]', dot: 'bg-[#00ff88]', ring: 'ring-[#00ff88]/25', glow: 'bg-[#00ff88]/20' }
    : isRunning
      ? { text: 'text-[#4488ff]', dot: 'bg-[#4488ff]', ring: 'ring-[#4488ff]/25', glow: 'bg-[#4488ff]/20' }
      : { text: 'text-[#666]', dot: 'bg-[#666]', ring: 'ring-[#2a2a2a]', glow: 'bg-transparent' }

  return (
    <div
      className={`flex items-center gap-3 pl-3 pr-4 py-1.5 rounded-lg bg-gradient-to-r from-[#161616] to-[#111] ring-1 ${accent.ring} max-w-[320px]`}
      title={
        isRunning
          ? `${modeLabel}: ${instrument ?? 'unknown instrument'} — ${activeStrategy?.strategy_id} / ${activeStrategy?.preset_name}`
          : 'No strategy is running in this container'
      }
    >
      {/* Status dot with a soft halo while running */}
      <span className="relative flex items-center justify-center w-2.5 h-2.5 shrink-0">
        {isRunning && (
          <span className={`absolute inline-flex w-full h-full rounded-full ${accent.glow} animate-ping`} />
        )}
        <span className={`relative inline-flex w-2 h-2 rounded-full ${accent.dot}`} />
      </span>

      <div className="flex flex-col min-w-0 leading-tight">
        {/* Line 1 — the instrument, the thing you actually want to see */}
        <div className="flex items-baseline gap-2 min-w-0">
          <span className={`font-mono text-sm font-semibold tracking-tight truncate ${isRunning ? 'text-white' : 'text-[#777]'}`}>
            {instrument ?? 'No instrument'}
          </span>
          <span className={`text-[9px] font-semibold uppercase tracking-[0.12em] shrink-0 ${accent.text}`}>
            {modeLabel}
          </span>
        </div>

        {/* Line 2 — which strategy is trading it */}
        <span className="text-[11px] text-[#8a8a8a] truncate">
          {activeStrategy
            ? <>
                {activeStrategy.strategy_id}
                <span className="text-[#4a4a4a]"> · </span>
                {activeStrategy.preset_name}
              </>
            : 'no strategy loaded'}
        </span>
      </div>

      {isRunning
        ? <Radio size={14} className={`${accent.text} shrink-0 ml-auto`} />
        : <CircleDashed size={14} className="text-[#3a3a3a] shrink-0 ml-auto" />}
    </div>
  )
}
